/**
 * Which language the interface is showing, readable outside React.
 *
 * `I18nProvider` owns the locale for rendering, but the Better Auth client is
 * a module, not a component, and it has to put the same value on every request
 * so the Worker can write mail in the language the reader is actually looking
 * at. Rather than mirror the React state into a module variable and risk the
 * two disagreeing, both read the same persisted source: `setLocale` writes to
 * `localStorage` before it re-renders, so what is stored is always what is on
 * screen.
 *
 * Before the reader has ever used the language switcher there is nothing
 * stored, and both fall back to the same rule.
 *
 * The order is:
 *
 *     ?lang=  ->  localStorage  ->  Bulgarian
 *
 * `?lang=` comes first because it is the only one that survives the trip out
 * to a mail client and back. A link in a confirmation email is followed in a
 * browser that has never seen this origin, where `localStorage` is empty.
 *
 * ## `navigator.language` is not in that list, and is not consulted anywhere
 *
 * A first-time visitor sees Bulgarian, whatever their browser asks for. The
 * switcher is visible on every page and works; that is the whole of the escape
 * hatch.
 *
 * This site is about Bulgarian editions, Bulgarian translators and Bulgarian
 * bookshops. An English speaker who lands here is largely outside the audience.
 * A Bulgarian on an English Windows — which is entirely ordinary here — is
 * squarely inside it, and under the old rule was shown English copy about
 * Bulgarian books.
 *
 * This function used to end with
 *
 *     return window.navigator.language.toLowerCase().startsWith('en') ? 'en' : DEFAULT_LOCALE
 *
 * directly underneath a comment reading "Bulgarian unless the browser clearly
 * prefers English. A reader arriving from a Bulgarian search result should not
 * land on the English copy." The comment described the intention. The line did
 * the opposite of it, for exactly the reader the comment named, and the pair
 * survived every review of this file. Worth remembering when a sentence here
 * reads well: a correct-sounding sentence is not a check.
 *
 * Do not reintroduce it as a "hint" or a tiebreaker either. There is one rule,
 * and no banner, geolocation check or first-visit chooser.
 *
 * Every touch of `localStorage` here is guarded, and the guard is around the
 * property access rather than only the call: Safari raises `SecurityError` on
 * `window.localStorage` itself when the reader has blocked cookies for the
 * site. This function runs inside the `useState` initialiser that wraps the
 * whole application, so a throw is not a wrong language — it is a blank page,
 * on the verification landing page that the entire sign-up loop depends on a
 * stranger reaching. Storage that cannot be read is treated as storage with
 * nothing in it, and a write that will not go through is dropped.
 */

import { DEFAULT_LOCALE, LOCALE_PARAM, isLocale, type Locale } from '../../shared/locale.ts'

export const LOCALE_STORAGE_KEY = 'chetiporeda.locale'

export function currentLocale(): Locale {
  // On the server (a page rendered by the Worker) there is no address bar and
  // no storage: Bulgarian, the default. The browser's first render then reads
  // the reader's own choice and switches if it differs.
  if (typeof window === 'undefined') return DEFAULT_LOCALE
  const requested = new URLSearchParams(window.location.search).get(LOCALE_PARAM)
  if (requested !== null && isLocale(requested)) {
    // Remembered on sight. The parameter is only on the URL the link opened;
    // the first navigation inside the app drops it, and without this the
    // visit would revert to the browser's language one page later.
    rememberLocale(requested)
    return requested
  }
  // Anything else in `?lang=` is ignored rather than refused. It picks
  // between two translation tables, so a value that is not one of them has
  // nothing to do except fall through to the rule below — it must not throw,
  // and it must not leave the reader looking at a blank page.
  const stored = storedLocale()
  if (stored && isLocale(stored)) return stored
  // Bulgarian. Not "Bulgarian unless the browser asks for English" — see the
  // header. A reader arriving from a Bulgarian search result must not land on
  // the English copy, and the browser's language does not say which reader this
  // is.
  return DEFAULT_LOCALE
}

/** Nothing stored, and unreachable storage, are the same answer here. */
function storedLocale(): string | null {
  try {
    return window.localStorage.getItem(LOCALE_STORAGE_KEY)
  } catch {
    return null
  }
}

export function rememberLocale(locale: Locale): void {
  try {
    window.localStorage.setItem(LOCALE_STORAGE_KEY, locale)
  } catch {
    // Dropped on purpose. A write can fail on its own — Safari's private
    // mode has historically refused one while allowing the read, and a full
    // quota does the same — and all that is lost is the choice outlasting
    // this page. The switcher still re-renders from React state, and a link
    // still carries the language itself. Taking the page down to report that
    // the preference was not saved would be the worse trade by a distance.
  }
}
